import { deleteAolItem, getAllAolItems } from 'src/api/aol/offlineAolApi';
import useGetAolData from 'src/api/aol/useGetAolData';
import useApi from 'src/api/useApi';
import { useAolStore } from 'src/context/AolContext';
import { formatAolUrlDate } from '../aol/aolDateUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { useSWService } from 'src/api/sw/useSWService';

const STALE_ITEM_MAX_AGE_MS = 5 * 24 * 60 * 60 * 1000;

/**
 * Detects whether an error response from the server indicates the request was
 * already processed (duplicate submission). This handles the case where the
 * service worker stored a request for offline retry after the server had already
 * processed it (e.g., network dropped on response, not on request).
 *
 * We treat these as success because the data IS saved on the server — retrying
 * would loop indefinitely. The patterns below cover SQL Server constraint errors
 * and common API conflict responses regardless of exact wording.
 */
const DUPLICATE_PATTERNS = [
    /primary\s*key/i,
    /duplicate\s*key/i,
    /cannot\s+insert\s+duplicate/i,
    /already\s+exists/i,
    /unique\s*constraint/i,
    /violation\s+of\s+.*constraint/i,
];

const isAlreadyProcessedError = (response: any): boolean => {
    if (!response?.isAxiosError) return false;
    const status = response.response?.status;
    if (status === 409) return true;
    if (status === 500) {
        const data = response.response?.data;
        const message = typeof data === 'string' ? data : data?.message ?? data?.Message ?? '';
        if (typeof message === 'string' && DUPLICATE_PATTERNS.some((pattern) => pattern.test(message))) {
            return true;
        }
    }
    return false;
};

const useSyncOfflineAolItems = () => {
    const { postApiData } = useApi();
    const { refreshRequestsContaining } = useSWService();
    const { getAolData } = useGetAolData();
    const { aolFilter } = useAolStore();
    const { selectedVessel } = useVesselStore();
    const syncAolItems = async (skipGetAolData?: boolean | null): Promise<void> => {
        try {
            const aolItems = await getAllAolItems();
            aolItems.sort((a, b) => a.lastUpdated - b.lastUpdated);

            for (const item of aolItems) {
                // Support both GUID keys (new) and integer ID keys (old, transition compat)
                const rawGuid = (item as any).noonReportGuid;
                const validGuid = rawGuid && rawGuid !== 'undefined' && rawGuid !== 'null' ? rawGuid : null;
                const itemKey = validGuid ?? item.noonReportId;

                if (!itemKey) {
                    console.warn('syncAolItems: Skipping item with no valid key — removing from store.', item);
                    try {
                        await deleteAolItem(rawGuid ?? item.noonReportId);
                    } catch (_) {
                        /* best effort */
                    }
                    continue;
                }

                if (item.lastUpdated < Date.now() - STALE_ITEM_MAX_AGE_MS) {
                    console.warn('syncAolItems: Purging stale item older than 5 days — will not be retried.', item);
                    try {
                        await deleteAolItem(itemKey);
                    } catch (_) {
                        /* best effort */
                    }
                    continue;
                }

                let allRequestsSuccessful = true;
                // Track synced noonReportId within this item's sync (for section URL patching)
                let syncedNoonReportId: number | undefined = item.noonReportId;

                try {
                    const requests = [...(item.saveNoonReportSectionRequests ?? []), ...(item?.updateSectionStatusRequests ?? [])];

                    if (item.deleteNoonReportRequest) {
                        const response = await postApiData({
                            fullUrl: item.deleteNoonReportRequest.url,
                            data: item.deleteNoonReportRequest.body,
                            skipRefreshCacheEndpoints: true,
                            shouldReturnError: true,
                            config: { headers: { 'X-Sync-Replay': 'true' } },
                        });
                        const status = response?.response?.status;
                        const isAlreadyAbsent = status === 404 || status === 410;
                        if (!isAlreadyAbsent && (!response || response instanceof Error || response?.isAxiosError)) {
                            allRequestsSuccessful = false;
                        }
                    } else {
                        if (item.addNoonReportRequest) {
                            const response = await postApiData({
                                fullUrl: item.addNoonReportRequest.url,
                                data: item.addNoonReportRequest.body,
                                skipRefreshCacheEndpoints: true,
                                shouldReturnError: true,
                                config: { headers: { 'X-Sync-Replay': 'true' } },
                            });
                            if (response?.metadata?.noonReportId) {
                                syncedNoonReportId = response.metadata.noonReportId;
                            } else {
                                allRequestsSuccessful = false;
                            }
                        } else if (!(item as any).noonReportGuid && item.noonReportId < 0) {
                            // Legacy: Invalid state — offline-created report (negative ID) without addNoonReportRequest
                            console.warn(
                                `Cannot sync offline noon report sections: noonReportId=${item.noonReportId} has no addNoonReportRequest. ` +
                                    'This item will be removed to prevent repeated sync failures.',
                                item
                            );
                            await deleteAolItem(itemKey);
                            continue;
                        }

                        if (allRequestsSuccessful) {
                            for (const request of requests) {
                                if (!request.body || Object.keys(request.body).length === 0) {
                                    continue;
                                }
                                // Patch noonReportId into section request URLs (backend still requires it)
                                const requestNoonReportId = +request.response.noonReportId;
                                if (syncedNoonReportId && (isNaN(requestNoonReportId) || requestNoonReportId !== syncedNoonReportId)) {
                                    if (request.body?.noonReportId) {
                                        request.body.noonReportId = syncedNoonReportId;
                                    }
                                    if (request.body?.sectionData?.noonReportId) {
                                        request.body.sectionData.noonReportId = syncedNoonReportId;
                                    }
                                }
                                const sectionResponse = await postApiData({
                                    fullUrl: request.url,
                                    data: request.body,
                                    skipRefreshCacheEndpoints: true,
                                    shouldReturnError: true,
                                    config: { headers: { 'X-Sync-Replay': 'true' } },
                                });
                                if (isAlreadyProcessedError(sectionResponse)) {
                                    // Server already has this data (PK violation / 409 conflict).
                                    // The original request was processed before the network dropped.
                                    console.info('syncAolItems: Section already saved on server, skipping.', request.url);
                                    continue;
                                }
                                if (!sectionResponse || sectionResponse?.isAxiosError) {
                                    allRequestsSuccessful = false;
                                    break;
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.warn('An unexpected error occurred while syncing item.', item, error);
                    allRequestsSuccessful = false;
                }

                if (allRequestsSuccessful) {
                    await deleteAolItem(itemKey);
                }
            }

            if (!skipGetAolData && selectedVessel) {
                await getAolData(selectedVessel.imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate), true);
                await refreshRequestsContaining(['getNoonReportsForShip', 'getNoonReportStatuses', 'getNoonReportSection', 'getNoonReportComments']);
            }
        } catch (error) {
            console.warn(error);
        }
    };

    return {
        syncAolItems,
    };
};

export default useSyncOfflineAolItems;
